import React, { useState, useEffect } from 'react'
import { CButton, CFormInput, CFormLabel, CFormSelect, CAlert } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../../shared/icons'
import { useBostonState } from '../lib/useBoston'
import { saveConfig } from '../lib/store'
import { refreshGateway } from '../lib/gateway'
import { useUI } from '../../../shared/ui/UIProvider'

const ENV_URLS = {
  local: 'http://localhost:3000/stg/',
  stg: 'https://stg.boston.basset.ws/',
  prod: 'https://boston.basset.ws/',
}

const MODEL_SETTING_KEYS = ['model_default', 'model_agent1_sst', 'model_agent2_ssc', 'model_agent3_gen', 'model_agent4_scriptlogic']

function envFromUrl(url) {
  if (url.includes('localhost')) return 'local'
  if (url.includes('stg.')) return 'stg'
  if (url.includes('boston.basset')) return 'prod'
  return 'stg'
}

function ConfigView() {
  const state = useBostonState()
  const { toast } = useUI()
  const [form, setForm] = useState({
    bostonApi: state.config.bostonApi,
    bostonAkey: state.config.bostonAkey,
    baseFolder: state.config.baseFolder,
    dashboardUrl: state.config.dashboardUrl,
  })
  const [aiHealth, setAiHealth] = useState(null)
  const [newKey, setNewKey] = useState('')
  const [busy, setBusy] = useState('')
  const [models, setModels] = useState({ model_default: '', model_agent1_sst: '', model_agent2_ssc: '', model_agent3_gen: '', model_agent4_scriptlogic: '', max_tokens: '' })
  const [savingModels, setSavingModels] = useState(false)

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function setModel(key, value) {
    setModels((m) => ({ ...m, [key]: value }))
  }

  async function loadModels() {
    try {
      const { biaGetSettings } = await import('../lib/biaApi')
      const settings = await biaGetSettings()
      const map = {}
        ; (settings || []).forEach((s) => { map[s.k || s.key] = s.v ?? s.value ?? '' })
      setModels({
        model_default: map.model_default || '',
        model_agent1_sst: map.model_agent1_sst || '',
        model_agent2_ssc: map.model_agent2_ssc || '',
        model_agent3_gen: map.model_agent3_gen || '',
        model_agent4_scriptlogic: map.model_agent4_scriptlogic || '',
        max_tokens: map.max_tokens || '',
      })
    } catch (_) { /* la pantalla igual funciona; el usuario puede cargar de cero */ }
  }

  async function saveModels() {
    setSavingModels(true)
    try {
      const { biaSetSetting } = await import('../lib/biaApi')
      for (const key of MODEL_SETTING_KEYS) {
        if (String(models[key] || '').trim()) await biaSetSetting(key, String(models[key]).trim())
      }
      if (String(models.max_tokens || '').trim()) await biaSetSetting('max_tokens', String(models.max_tokens).trim())
      toast('Modelos y tokens guardados en la DB.', 'success')
      loadModels()
    } catch (error) {
      toast('No se pudo guardar: ' + error.message, 'danger')
    } finally {
      setSavingModels(false)
    }
  }

  useEffect(() => { loadModels() }, [])

  function save() {
    saveConfig({
      mode: 'standalone',
      bostonApi: form.bostonApi.trim(),
      bostonAkey: form.bostonAkey.trim(),
      baseFolder: form.baseFolder.trim() || 'admin_v2_local_library\\',
      dashboardUrl: form.dashboardUrl.trim(),
    })
    refreshGateway()
    toast('Configuracion guardada.', 'success')
  }

  async function checkAIAdmin() {
    setBusy('health')
    try {
      const { biaHealth, biaGetSettings } = await import('../lib/biaApi')
      const health = await biaHealth()
      let settings = []
      try {
        settings = await biaGetSettings()
      } catch (error) {
        health.settingsError = error.message
      }
      setAiHealth({ ...health, settings })
    } catch (error) {
      setAiHealth({ ok: false, error: error.message })
    } finally {
      setBusy('')
    }
  }

  async function saveBackendKey() {
    if (!newKey.trim() || newKey.trim().length < 20) {
      toast('Key invalida.', 'warning')
      return
    }
    setBusy('key')
    try {
      const { biaSetApiKey } = await import('../lib/biaApi')
      const result = await biaSetApiKey(newKey.trim())
      setNewKey('')
      toast('API key guardada en backend (' + (result.last4 ? '...' + result.last4 : 'write-only') + ').', 'success')
      checkAIAdmin()
    } catch (error) {
      toast('No se pudo guardar: ' + error.message, 'danger')
    } finally {
      setBusy('')
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Configuracion</h1>
          <p>Backend Sabre, conexion administrativa /admin/ai y generacion local de archivos.</p>
        </div>
        <CButton color="success" className="btn-brand" onClick={save}>
          <CIcon icon={icon('cilSave')} /> Guardar
        </CButton>
      </div>

      <div className="card-x">
        <h3>Backend Sabre</h3>
        <div className="bia-grid-2">
          <div>
            <CFormLabel>Entorno</CFormLabel>
            <CFormSelect
              value={envFromUrl(form.bostonApi)}
              onChange={(event) => set('bostonApi', ENV_URLS[event.target.value] || form.bostonApi)}
            >
              <option value="local">Local</option>
              <option value="stg">STG</option>
              <option value="prod">PROD</option>
            </CFormSelect>
            <small className="text-muted">Cambia la URL de la API de Boston.</small>
          </div>
          <div>
            <CFormLabel>Boston API URL</CFormLabel>
            <CFormInput className="bia-mono" value={form.bostonApi} onChange={(event) => set('bostonApi', event.target.value)} />
          </div>
          <div className="bia-span-2">
            <CFormLabel>Boston API Key</CFormLabel>
            <CFormInput className="bia-mono" value={form.bostonAkey} onChange={(event) => set('bostonAkey', event.target.value)} />
          </div>
        </div>
      </div>

      <div className="card-x">
        <h3>Administracion AI</h3>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Prompts, settings, knowledge, review queue y audit log se administran mediante public-api-boston <code>/admin/ai</code>.
          La key de Anthropic se guarda en backend y no viaja al navegador.
        </p>
        <div className="mb-3" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CButton color="secondary" variant="outline" size="sm" disabled={busy !== ''} onClick={checkAIAdmin}>
            <CIcon icon={icon('cilReload')} /> {busy === 'health' ? 'Probando...' : 'Probar /admin/ai'}
          </CButton>
        </div>
        {aiHealth ? (
          <CAlert color={aiHealth.ok === false ? 'warning' : 'success'} className="py-2" style={{ fontSize: 13 }}>
            API: {aiHealth.ok === false ? 'sin respuesta' : 'ok'} {aiHealth.db ? ' - DB: ' + aiHealth.db : ''}
            {aiHealth.crypto ? ' - cifrado: ' + aiHealth.crypto : ''}
            {aiHealth.error ? <div>{aiHealth.error}</div> : null}
            {aiHealth.settingsError ? <div>Settings: {aiHealth.settingsError}</div> : null}
            {aiHealth.settings && aiHealth.settings.length ? (
              <div style={{ marginTop: 6 }}>
                {aiHealth.settings.map((item) => (
                  <div key={item.k || item.key}>
                    <code>{item.k || item.key}</code>: {item.secret ? (item.status || 'write-only') : <code>{String(item.v ?? item.value ?? '')}</code>}
                  </div>
                ))}
              </div>
            ) : null}
          </CAlert>
        ) : null}
        <div className="mb-2">
          <CFormLabel>API key de Anthropic en backend</CFormLabel>
          <div style={{ display: 'flex', gap: 8 }}>
            <CFormInput
              className="bia-mono"
              type="password"
              value={newKey}
              placeholder="sk-ant-... (write-only)"
              autoComplete="off"
              onChange={(event) => setNewKey(event.target.value)}
            />
            <CButton color="success" className="btn-brand" disabled={busy !== '' || !newKey.trim()} onClick={saveBackendKey}>
              <CIcon icon={icon('cilLockLocked')} /> {busy === 'key' ? 'Guardando...' : 'Guardar'}
            </CButton>
          </div>
          <small className="text-muted">
            La generacion AI se ejecuta desde backend via /admin/ai assistant/generate; la key no se expone al frontend.
          </small>
        </div>
      </div>

      <div className="card-x">
        <h3>Modelos y tokens (IA)</h3>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Se guardan en la DB (<code>ai_settings</code>) y los usa el backend — sin tocar SQL.
          Sugeridos: <code>claude-opus-5</code>, <code>claude-sonnet-4-5</code>, <code>claude-haiku-4-5-20251001</code>.
          Dejá un agente vacío para que use el modelo por defecto.
        </p>
        <div className="bia-grid-2">
          <div>
            <CFormLabel>Modelo por defecto (model_default)</CFormLabel>
            <CFormInput className="bia-mono" value={models.model_default} placeholder="claude-opus-5" onChange={(event) => setModel('model_default', event.target.value)} />
          </div>
          <div>
            <CFormLabel>Max tokens</CFormLabel>
            <CFormInput className="bia-mono" type="number" value={models.max_tokens} placeholder="(vacio = maximo del modelo)" onChange={(event) => setModel('max_tokens', event.target.value)} />
          </div>
          <div>
            <CFormLabel>Agente 1 — interprete .sst</CFormLabel>
            <CFormInput className="bia-mono" value={models.model_agent1_sst} placeholder="(usa el default)" onChange={(event) => setModel('model_agent1_sst', event.target.value)} />
          </div>
          <div>
            <CFormLabel>Agente 2 — interprete .ssc</CFormLabel>
            <CFormInput className="bia-mono" value={models.model_agent2_ssc} placeholder="(usa el default)" onChange={(event) => setModel('model_agent2_ssc', event.target.value)} />
          </div>
          <div>
            <CFormLabel>Agente 3 — generador</CFormLabel>
            <CFormInput className="bia-mono" value={models.model_agent3_gen} placeholder="(usa el default)" onChange={(event) => setModel('model_agent3_gen', event.target.value)} />
          </div>
          <div>
            <CFormLabel>Agente 4 — logica</CFormLabel>
            <CFormInput className="bia-mono" value={models.model_agent4_scriptlogic} placeholder="(usa el default)" onChange={(event) => setModel('model_agent4_scriptlogic', event.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <CButton color="success" className="btn-brand" disabled={savingModels} onClick={saveModels}>
            <CIcon icon={icon('cilSave')} /> {savingModels ? 'Guardando...' : 'Guardar modelos'}
          </CButton>
        </div>
      </div>

      <div className="card-x">
        <h3>Generacion local + Dashboard</h3>
        <div className="mb-3">
          <CFormLabel>Etiqueta de carpeta local/exportacion</CFormLabel>
          <CFormInput
            className="bia-mono"
            value={form.baseFolder}
            placeholder="admin_v2_local_library\\"
            onChange={(event) => set('baseFolder', event.target.value)}
          />
          <small className="text-muted">Se usa para biblioteca local y manifest; la publicacion remota queda pendiente para fase posterior.</small>
        </div>
        <div className="mb-3">
          <CFormLabel>URL del dashboard (opcional)</CFormLabel>
          <CFormInput
            className="bia-mono"
            value={form.dashboardUrl}
            placeholder="https://dashboard.basset.ws/scripts"
            onChange={(event) => set('dashboardUrl', event.target.value)}
          />
        </div>
        <CAlert color="info" className="mb-0">
          Los scripts se generan, previsualizan y descargan localmente desde admin_v2. Publicacion remota pendiente para fase posterior.
        </CAlert>
      </div>
    </>
  )
}

export default ConfigView
